import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { IAdmin } from 'src/app/state/admin.model';
import { handleError } from './akita-error-helper';
import { CollectionState } from './collection-state';

@Injectable({ providedIn: 'root' })
export class AdminService {
  private readonly state = new CollectionState<IAdmin>();

  public readonly admins$ = this.state.items$;
  public readonly loading$ = this.state.loading$;
  public readonly error$ = this.state.error$;

  public constructor(private readonly http: HttpClient) {}

  public load(): Observable<void> {
    this.state.setLoading(true);

    return this.http.get<IAdmin[]>('api/admins').pipe(
      tap((admins) => {
        this.state.set(admins);
      }),
      catchError((error: unknown) => {
        handleError(this.state, error, {
          default: () => {
            const statusText = this.hasStatusText(error)
              ? String(error.statusText)
              : 'Unknown error';

            this.state.setError(statusText);
          },
        });

        return of(undefined);
      }),
      map(() => undefined)
    );
  }

  public hasStatusText(error: unknown): error is { statusText: unknown } {
    return this.isObjectButNotNull(error) && 'statusText' in error;
  }

  public create(admin: IAdmin): Observable<void> {
    return this.http.post<IAdmin>('api/admins', admin).pipe(
      tap((result) => {
        this.state.add(result);
      }),
      map(() => undefined)
    );
  }

  private isObjectButNotNull(value: unknown): value is object {
    return !!value && typeof value === 'object';
  }

  public delete(username: string): Observable<void> {
    return this.http.delete(`api/admins/${username}`).pipe(
      tap(() => {
        this.state.removeWhere((admin) => admin.username === username);
      }),
      map(() => undefined)
    );
  }

  public get(username: string): Observable<IAdmin> {
    return this.http.get<IAdmin>(`api/admins/${username}`);
  }
}
